// LAYOUT DO MOVIMENTO — o arquivo que a Atlas entrega a prefeitura.
//
//   ADF        autoriza o desconto do contrato em folha
//   MOVIMENTO  informa mensalmente a prefeitura quais parcelas processar
//   RETORNO    informa a Atlas quais parcelas foram efetivamente descontadas
//
// E o layout PADRAO de saida, e o unico que o dominio conhece (decisao do
// cliente, 27/08/2026). Desde 02/09/2026 um ente cuja folha nao consegue le-lo
// pode receber um layout ADAPTADO, traduzido na borda a partir deste mesmo
// modelo — ver specs/adrs/0007-layouts-adaptados-na-borda.md. O que NAO voltou
// foi o perfil dentro do dominio: `RemessaEntrada` segue sem saber de ente.
//
// Este arquivo e' so' a DEFINICAO das colunas; a serializacao mora em
// `movimento.ts`, e o modelo (`RemessaEntrada`) segue neutro.
//
// A forma saiu do arquivo real do ConsigFacil, e a DIRECAO dele foi medida,
// nao suposta — o nome sugere o contrario e ja causou duas leituras erradas:
//
//   · Delimitador. Os tres arquivos do zip de retorno sao todos `;` (44, 10 e 8
//     pontos-e-virgula na primeira linha). O `movimento` e `|`. Produtores
//     diferentes.
//   · Direcao. O zip baixado era da competencia 202607 (a competencia esta no
//     nome da terceira entrada, `rubrica_funcionarios20267`) e o `movimento` era
//     202608 — o mes SEGUINTE. Nao sao par da mesma realidade.
//   · Conteudo. As 50 linhas do retorno 202607 aparecem no movimento 202608 com
//     a parcela em EXATAMENTE +1, zero excecao, zero linha removida, mais UMA
//     linha nova (`1300535`, verba `0267`, parcela 1 de 117). O par anterior
//     repete a assinatura: 202605 -> 202606, 49 linhas em +1, uma nova
//     (`1300520`, parcela 1 de 120), zero saidas.
//   · Tipo. O campo `I` esta em TODAS as linhas, inclusive nas que ja existiam.
//     Se fosse "inclusao de contrato novo", so a linha nova teria. Nao e delta
//     de mudancas: e a posicao inteira reinstruida todo mes.
//
// Que e exatamente o que o ADR-0001 descreve pra remessa da Atlas: "a lista do
// que precisa ser descontado naquele mes — os contratos novos e os que ja vem de
// meses anteriores".
//
// Larguras: medidas em 150 linhas de tres arquivos reais (202605, 202606,
// 202608). Todas fixas menos a matricula, que aparece com 5 e com 7 digitos.
//
// Encoding: latin1, como o pacote real. Importa porque campo de largura fixa
// conta CARACTERES: em UTF-8 um nome acentuado empurraria os campos seguintes.
// O CONCEITO DE PERFIL FOI DESCARTADO em 27/08/2026: este layout e o UNICO
// formato de saida, pra toda prefeitura. Havia um `PERFIS_REMESSA` aqui com
// "atlas" e "consigfacil"; o layout atlas morreu junto.
export const MOVIMENTO_SEPARADOR = "|";
/** O pacote real e latin1. Campo de largura fixa conta caractere, nao byte. */
export const MOVIMENTO_ENCODING = "latin1";
/** Sobe quando coluna entra/sai ou regra de montagem muda. */
export const MOVIMENTO_LAYOUT_VERSAO = "1.0";
/**
 * Layout do `movimento`, campo a campo, como medido no arquivo real.
 *
 * Fonte unica: o serializador e a tabela do documento tecnico saem DESTE array.
 * O papel nao consegue descrever uma coluna diferente da que o codigo escreve.
 */
export const COLUNAS_MOVIMENTO = [
    {
        nome: "matricula", coluna: 1, kind: "alfa", obrigatorio: true,
        descricao: "Matrícula do servidor, como a folha a grava. **Único campo de largura variável** — no arquivo real aparece com 5 e com 7 dígitos. Não é preenchida nem truncada.",
    },
    {
        nome: "nome", coluna: 2, kind: "alfa", largura: 50, preenche: "espacos",
        descricao: "Nome do servidor, alinhado à esquerda e completado com espaços até 50 caracteres. Nome mais longo é truncado em 50.",
    },
    {
        nome: "cpf", coluna: 3, kind: "alfa", largura: 14, obrigatorio: true,
        descricao: "CPF **com máscara**, no formato `000.000.000-00`. É a única chave nacional do servidor — matrícula é numerada por folha, e um município com mais de uma folha repete o mesmo número em pessoas diferentes.",
    },
    {
        nome: "tipoMovimento", coluna: 4, kind: "codigo", codigos: ["I", "A", "E"], largura: 10, preenche: "espacos",
        descricao: "`I` instruir o desconto · `A` valor ou cronograma mudaram · `E` **parar de descontar** (quitada, desligamento, suspensão). Completado com espaços até 10. O `I` vale para **toda** linha, não só para a dívida que começou neste mês: o arquivo é a posição inteira, não um delta. O `E` existe para o importador que apenas ACRESCENTA o que vem no arquivo — esse não pararia pela ausência da linha.",
    },
    {
        nome: "parcelaNumero", coluna: 5, kind: "num", obrigatorio: true, largura: 3, preenche: "zeros",
        descricao: "Número da parcela a descontar nesta competência.",
    },
    {
        nome: "parcelasTotal", coluna: 6, kind: "num", obrigatorio: true, largura: 3, preenche: "zeros",
        descricao: "Total de parcelas do contrato.",
    },
    {
        nome: "parcelasRestantes", coluna: 7, kind: "num", obrigatorio: true, largura: 3, preenche: "zeros",
        descricao: "Parcelas que ainda faltam, **incluindo a deste mês**: `total − atual + 1`. Confirmado em 150 de 150 linhas dos três `movimento` reais, e em 301 de 301 na série de seis arquivos do ADR-0003. Ler como `total − atual` erra por um em todo lugar.",
    },
    {
        nome: "valorParcela", coluna: 8, kind: "dinheiro", obrigatorio: true, largura: 15, preenche: "zeros",
        descricao: "Valor da parcela: `000000000233,17`.",
    },
    {
        nome: "referencia", coluna: 9, kind: "alfa", largura: 22, preenche: "espacos",
        descricao: "Número do contrato na origem. **Vem vazio nas 150 linhas medidas**; a Atlas escreve aqui a **ADF**. É o que permite ao retorno casar a dívida sem ambiguidade quando o servidor tem duas na mesma verba.",
    },
    {
        nome: "codigoVerba", coluna: 10, kind: "alfa", obrigatorio: true, largura: 4,
        descricao: "Código da verba/rubrica de desconto na folha, com os zeros à esquerda (`0234`).",
    },
    {
        nome: "competencia", coluna: 11, kind: "competencia", obrigatorio: true, largura: 6,
        descricao: "Competência do desconto, repetida em toda linha — o arquivo não tem cabeçalho.",
    },
];
//# sourceMappingURL=movimento-layout.js.map